// store/forms/samplingEventForm/wells/actions.js

import { v4 as uuidv4 } from "uuid";
import {
  Wells,
  FieldMeasurements,
  SamplingEmployeesSignature,
} from "../../../../models/SamplingEvent/Wells";
import { SamplingGroupWells } from "../../../../models/SamplingEvent/SamplingGroup";
// Import shared filter actions factory
import { makeWellsFiltersActions } from "../filter/samplingEventWellsFiltersActions";

// Factory for Wells actions. Mirrors other tab patterns.
export const makeWellsActions = (api) => {
  // Create shared filter actions for wells tab
  const filterActions = makeWellsFiltersActions(api.setWellsUi, api.bump);

  return {
    // Spread in all shared filter actions (toggleFilterMenu, openFilterMenu, closeFilterMenu, applyFilters, resetFilters, setSort, resetSort, resetAll)
    ...filterActions,

    // --- UI actions ---
    setExpandedWell: (wellId) => {
      api.setWellsUi((u) => ({
        expandedWellId: u.expandedWellId === wellId ? null : wellId,
      }));
      api.bump();
    },

    setExpandedGroupId: (groupId) => {
      api.setWellsUi((u) => ({
        expandedGroupId: u.expandedGroupId === groupId ? null : groupId,
      }));
      api.bump();
    },

    setActiveTab: (tab) => {
      api.setWellsUi({ activeTab: tab });
      api.bump();
    },

    setActiveTab: (tab) => {
      api.setWellsUi({ activeTab: tab });
      api.bump();
    },

    setSelectedWellIds: (ids) => {
      api.setWellsUi({ selectedWellIds: Array.isArray(ids) ? ids : [] });
      api.bump();
    },

    // Modal controls
    openFieldMeasurementModal: (isAddMode = false) => {
      api.setWellsUi({
        isFieldMeasurementModalOpen: true,
        isFieldMeasurementAddMode: isAddMode,
      });
      api.bump();
    },

    closeFieldMeasurementModal: () => {
      api.setWellsUi({
        isFieldMeasurementModalOpen: false,
        isFieldMeasurementAddMode: false,
      });
      api.bump();
    },

    openFieldMeasurementConfirmationModal: () => {
      api.setWellsUi({
        isFieldMeasurementConfirmationModalOpen: true,
      });
      api.bump();
    },

    closeFieldMeasurementConfirmationModal: () => {
      api.setWellsUi({
        isFieldMeasurementConfirmationModalOpen: false,
      });
      api.bump();
    },

    openSamplerSignatureModal: () => {
      api.setWellsUi({
        isSamplerSignatureModalOpen: true,
      });
      api.bump();
    },

    closeSamplerSignatureModal: () => {
      api.setWellsUi({
        isSamplerSignatureModalOpen: false,
      });
      api.bump();
    },

    openSamplerSignatureConfirmationModal: () => {
      api.setWellsUi({
        isSamplerSignatureConfirmationModalOpen: true,
      });
      api.bump();
    },

    closeSamplerSignatureConfirmationModal: () => {
      api.setWellsUi({
        isSamplerSignatureConfirmationModalOpen: false,
      });
      api.bump();
    },

    // QC Signature Modal
    openQcSignatureModal: () => {
      api.setWellsUi({
        isQcSignatureModalOpen: true,
      });
      api.bump();
    },

    closeQcSignatureModal: () => {
      api.setWellsUi({
        isQcSignatureModalOpen: false,
      });
      api.bump();
    },

    openQcSignatureConfirmationModal: () => {
      api.setWellsUi({
        isQcSignatureConfirmationModalOpen: true,
      });
      api.bump();
    },

    closeQcSignatureConfirmationModal: () => {
      api.setWellsUi({
        isQcSignatureConfirmationModalOpen: false,
      });
      api.bump();
    },

    // Marking complete modal for opening
    openConfirmationModal: () => {
      api.setWellsUi({
        isConfirmationModalOpen: true,
      });
      api.bump();
    },

    closeConfirmationModal: () => {
      api.setWellsUi({
        isConfirmationModalOpen: false,
      });
      api.bump();
    },

    // --- Add Well to existing sampling event ---

    /**
     * Add one or more wells from the sampling site catalog to an existing
     * sampling event. Each selected well is attached to the chosen sampling
     * group and gets optimistically pushed to event.samplingEventWells. A
     * single patch is emitted per well; the server responds with a broadcast
     * that the wells slice's handleAddWellBroadcast reconciles against the
     * optimistic rows by id.
     *
     * @param {object} params
     * @param {Array<object>} params.wells - Site catalog wells (snake_case from API)
     * @param {string} params.samplingGroupId - The group these wells join
     * @param {function} params.emitPatch - Autosave emitPatch from tabProps
     * @returns {Promise<{ok: boolean, added: number, error?: string}>}
     */
    addSamplingEventWells: async ({ wells, samplingGroupId, emitPatch }) => {
      const event = api.getCurrentEvent();
      if (!event?.id) return { ok: false, error: "no_event" };
      if (!Array.isArray(wells) || wells.length === 0) {
        return { ok: false, error: "no_wells_selected" };
      }
      if (!samplingGroupId) return { ok: false, error: "no_group_selected" };

      const group = event.samplingGroups.find((g) => g.id === samplingGroupId);
      if (!group) return { ok: false, error: "group_not_found" };

      // Build month-year suffix from event.startDate for well_sample_id.
      // event.startDate format from the API has historically been "MM/DD/YYYY".
      const buildSampleIdSuffix = (startDate) => {
        if (!startDate) return "";
        if (startDate instanceof Date) {
          const m = String(startDate.getMonth() + 1).padStart(2, "0");
          return `${m}${startDate.getFullYear()}`;
        }
        const str = String(startDate);
        const slash = str.split("/");
        if (slash.length === 3) {
          return `${slash[0].padStart(2, "0")}${slash[2]}`;
        }
        const dash = str.split("-");
        if (dash.length === 3) {
          return `${dash[1].padStart(2, "0")}${dash[0]}`;
        }
        return "";
      };

      const sampleIdSuffix = buildSampleIdSuffix(event.startDate);

      let successCount = 0;
      for (const catalogWell of wells) {
        const wellId =
          catalogWell.well_id || catalogWell.wellId || catalogWell.id;
        if (!wellId) continue;

        const duplicate = event.samplingEventWells.some(
          (w) => w.wellId === wellId
        );
        if (duplicate) continue;

        const newRowId = uuidv4();
        const wellSampleId = sampleIdSuffix
          ? `${wellId}_${sampleIdSuffix}`
          : "";

        const newWell = new Wells({
          ...catalogWell,
          id: newRowId,
          sampling_event_id: event.id,
          well_id: wellId,
          well_sample_id: wellSampleId,
          field_measurements: [
            {
              id: uuidv4(),
              sampling_event_id: event.id,
              sampling_event_well_id: newRowId,
              well_id: wellId,
              sort_order: 0,
            },
          ],
          employee_signatures: [],
        });
        event.samplingEventWells.push(newWell);

        const newGroupWell = new SamplingGroupWells({
          id: uuidv4(),
          sampling_group_id: samplingGroupId,
          well_id: wellId,
          sampling_site_name: event.wellSite || catalogWell.site_name || "",
          well_name: catalogWell.site_name || "",
        });
        group.wells.push(newGroupWell);

        api.bumpRevision();

        if (typeof emitPatch === "function") {
          try {
            const result = await emitPatch({
              patch_type: "sampling_event_add_well",
              action: "add",
              sampling_event_id: event.id,
              sampling_group_id: samplingGroupId,
              id: newRowId,
              well_id: wellId,
            });
            const isSuccess =
              result === true ||
              result?.success === true ||
              result?.ok === true;
            if (isSuccess) successCount += 1;
          } catch (err) {
            console.error("Error emitting add well patch:", err);
          }
        }
      }

      return { ok: true, added: successCount };
    },

    /**
     * Handle the broadcast that confirms a well was added on the server.
     * Reconciles by `id`: if the optimistic Wells row exists, leave it in
     * place (its catalog data already populated the model); if it does not
     * (e.g. another user added the well), instantiate it and append.
     */
    handleAddWellBroadcast: (patch) => {
      const event = api.getCurrentEvent();
      if (!event) return;

      const wellPayload = patch?.sampling_event_well;
      const groupWellPayload = patch?.sampling_group_well;

      if (wellPayload?.id) {
        const existing = event.samplingEventWells.find(
          (w) => w.id === wellPayload.id
        );
        if (!existing) {
          event.samplingEventWells.push(new Wells(wellPayload));
        }
      }

      if (groupWellPayload?.sampling_group_id && groupWellPayload?.id) {
        const group = event.samplingGroups.find(
          (g) => g.id === groupWellPayload.sampling_group_id
        );
        if (group) {
          const groupWellExists = group.wells.some(
            (w) => w.id === groupWellPayload.id
          );
          if (!groupWellExists) {
            group.wells.push(new SamplingGroupWells(groupWellPayload));
          }
        }
      }

      api.bumpRevision();
    },

    // --- Temporary State Actions for Field Measurements ---

    /**
     * Creates the initial temporary state for the field measurement modal.
     * @param {object|null} existingMeasurement - The existing measurement to edit (null for add mode)
     * @param {string} wellId - The well ID for this measurement
     * @param {object} well - The well object
     * @returns {object} - The initial temp state
     */
    createInitialFieldMeasurementTempState: (
      existingMeasurement,
      wellId,
      well
    ) => {
      if (existingMeasurement) {
        // Edit mode - copy existing measurement data
        return {
          isNew: false,
          measurementId: existingMeasurement.rowId,
          wellId: wellId,
          data: {
            measurementDate: existingMeasurement.measurementDate || "",
            measurementTime: existingMeasurement.measurementTime || "",
            extractedVolume: existingMeasurement.extractedVolume || null,
            extractedVolumeUnit:
              existingMeasurement.extractedVolumeUnit || null,
            dtw: existingMeasurement.dtw || null,
            temperature: existingMeasurement.temperature || null,
            ph: existingMeasurement.ph || null,
            specificConductance:
              existingMeasurement.specificConductance || null,
            specificConductanceUnit:
              existingMeasurement.specificConductanceUnit || null,
            orp: existingMeasurement.orp || null,
            doMeasurement: existingMeasurement.doMeasurement || null,
            turbidity: existingMeasurement.turbidity || null,
            sortOrder: existingMeasurement.sortOrder ?? null,
          },
        };
      } else {
        // Add mode - create blank temp state with new UUID
        const newId = uuidv4();
        const currentDate = new Date();
        const currentTime = currentDate.toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        });
        const formattedDate = currentDate.toLocaleDateString("en-US", {
          month: "2-digit",
          day: "2-digit",
          year: "numeric",
        });

        return {
          isNew: true,
          measurementId: newId,
          wellId: wellId,
          data: {
            measurementDate: formattedDate,
            measurementTime: currentTime,
            extractedVolume: null,
            extractedVolumeUnit: null,
            dtw: null,
            temperature: null,
            ph: null,
            specificConductance: null,
            specificConductanceUnit: null,
            orp: null,
            doMeasurement: null,
            turbidity: null,
            sortOrder: well?.fieldMeasurements?.length ?? 0,
          },
        };
      }
    },

    /**
     * Commits the temporary field measurement state to the actual data model and server.
     * @param {object} tempState - The temporary state from the modal
     * @param {function} emitPatch - The function to emit patches to the server
     * @returns {object} - Result object with ok status
     */
    commitTempFieldMeasurement: async (tempState, emitPatch) => {
      const event = api.getCurrentEvent();
      if (!event) {
        return { ok: false, error: "No active sampling event" };
      }

      const { isNew, measurementId, wellId, data } = tempState;

      const well = event.samplingEventWells.find((w) => w.id === wellId);
      if (!well) {
        return { ok: false, error: "Well not found" };
      }

      let fieldMeasurement;

      if (isNew) {
        // Check if measurement already exists (prevent duplicates from double-clicks)
        const existingMeasurement = well.fieldMeasurements.find(
          (fm) => fm.rowId === measurementId
        );

        if (existingMeasurement) {
          // Update existing instead of creating duplicate
          fieldMeasurement = existingMeasurement;
        } else {
          // Create new field measurement
          fieldMeasurement = new FieldMeasurements({
            id: measurementId,
            well_id: well.wellId,
            sampling_event_well_id: well.id,
            sampling_event_id: event.id,
            sort_order: data.sortOrder,
          });
          well.fieldMeasurements.push(fieldMeasurement);
        }
      } else {
        // Edit mode - find existing measurement
        fieldMeasurement = well.fieldMeasurements.find(
          (fm) => fm.rowId === measurementId
        );
        if (!fieldMeasurement) {
          return { ok: false, error: "Field measurement not found" };
        }
      }

      // Apply all data from tempState to the measurement
      fieldMeasurement.setMeasurementDate(data.measurementDate);
      fieldMeasurement.setMeasurementTime(data.measurementTime);
      fieldMeasurement.setExtractedVolume(data.extractedVolume);
      fieldMeasurement.setExtractedVolumeUnit(data.extractedVolumeUnit);
      fieldMeasurement.setDtw(data.dtw);
      fieldMeasurement.setTemperature(data.temperature);
      fieldMeasurement.setPh(data.ph);
      fieldMeasurement.setSpecificConductance(data.specificConductance);
      fieldMeasurement.setSpecificConductanceUnit(data.specificConductanceUnit);
      fieldMeasurement.setOrp(data.orp);
      fieldMeasurement.setDoMeasurement(data.doMeasurement);
      fieldMeasurement.setTurbidity(data.turbidity);
      fieldMeasurement.setSortOrder(data.sortOrder);

      // Emit patch to server (upsert handles both add and update)
      const action = isNew ? "add" : "update";
      const patch = fieldMeasurement.toPatch(well.id, action);

      if (typeof emitPatch === "function") {
        try {
          const result = await emitPatch(patch);
          const isSuccess =
            result === true || result?.success === true || result?.ok === true;
          if (!isSuccess) {
            // Log warning but don't fail - local state is updated
            console.warn(
              "Field measurement patch may not have saved:",
              result?.error || "Unknown issue"
            );
          }
        } catch (err) {
          console.error("Error emitting field measurement patch:", err);
          // Continue - local state is already updated
        }
      }

      api.bumpRevision();
      return { ok: true, fieldMeasurement };
    },

    removeFieldMeasurement: (wellId, measurementId, emitPatch) => {
      const event = api.getCurrentEvent();
      if (!event) return { ok: false, error: "no_event" };
      const well = event.samplingEventWells.find((w) => w.id === wellId);
      if (!well) return { ok: false, error: "well_not_found" };

      // Find the field measurement first to get its data for the patch
      const fieldMeasurement = well.fieldMeasurements.find(
        (fm) => fm.rowId === measurementId
      );
      if (!fieldMeasurement)
        return { ok: false, error: "field_measurement_not_found" };

      // Send the patch before removing
      if (emitPatch) {
        emitPatch(fieldMeasurement.toPatch(well.id, "remove"));
      }

      // Now remove it from the well
      well.removeFieldMeasurement(measurementId);

      api.bump();
      return { ok: true, fieldMeasurement };
    },

    // Updating the field measurement on the server with all information in the data class.
    sendFieldMeasurementPatch: (wellId, measurementId, action, emitPatch) => {
      const event = api.getCurrentEvent();
      if (!event) return { ok: false, error: "no_event" };
      const well = event.samplingEventWells.find((w) => w.id === wellId);
      if (!well) return { ok: false, error: "well_not_found" };
      const fieldMeasurement = well.fieldMeasurements.find(
        (fm) => fm.rowId === measurementId
      );
      if (!fieldMeasurement)
        return { ok: false, error: "field_measurement_not_found" };
      if (emitPatch) {
        emitPatch(fieldMeasurement.toPatch(well.id, action));
      }
      api.bump();
      return { ok: true, fieldMeasurement };
    },

    // Sampler Signatures (employee)
    addSamplerSignature: (wellId, emitPatch) => {
      const event = api.getCurrentEvent();
      if (!event) return null;
      const well = event.samplingEventWells.find((w) => w.id === wellId);
      if (!well) return null;
      const samplerSignature = well.addBlankEmployeeSignature?.();
      if (!samplerSignature) return null;
      if (emitPatch) {
        emitPatch(samplerSignature.toPatch(event.id, "add"));
      }
      api.bump();
      return samplerSignature;
    },

    updateSamplerSignature: (
      wellId,
      signatureId,
      fieldsAndValues,
      emitPatch = null
    ) => {
      const event = api.getCurrentEvent();
      if (!event) return { ok: false, error: "no_event" };
      const well = event.samplingEventWells.find((w) => w.id === wellId);
      if (!well) return { ok: false, error: "well_not_found" };
      const samplerSignature = well.employeeSignatures.find(
        (signature) => signature.rowId === signatureId
      );
      if (!samplerSignature)
        return { ok: false, error: "sampler_signature_not_found" };
      fieldsAndValues.forEach(({ field, value }) => {
        switch (field) {
          case "employeeId":
            samplerSignature.setSelectedEmployee(value);
            break;
          case "signature":
            samplerSignature.setSignature(value);
            break;
        }
      });
      if (emitPatch) {
        emitPatch(samplerSignature.toPatch(event.id, "update"));
      }
      api.bump();
      return { ok: true, samplerSignature };
    },

    removeSamplerSignature: async (wellId, signatureId, emitPatch = null) => {
      const event = api.getCurrentEvent();
      if (!event) return { ok: false, error: "no_event" };
      const well = event.samplingEventWells.find((w) => w.id === wellId);
      if (!well) return { ok: false, error: "well_not_found" };
      const samplerSignature = well.employeeSignatures.find(
        (signature) => signature.rowId === signatureId
      );
      if (!samplerSignature)
        return { ok: false, error: "sampler_signature_not_found" };
      if (emitPatch) {
        emitPatch(samplerSignature.toPatch(event.id, "remove"));
      }
      well.removeEmployeeSignature(signatureId);
      api.bump();
      return { ok: true, samplerSignature };
    },

    // Validation and Mark Compete function wrapper for our well card components
    validateSamplingAndMarkComplete: (
      wellId,
      fieldsToValidate,
      fieldMeasurementErrorArray
    ) => {
      const event = api.getCurrentEvent();
      if (!event) return { ok: false, error: "no_event" };

      const well = event.samplingEventWells.find((w) => w.id === wellId);
      if (!well) return { ok: false, error: "well_not_found" };

      return well.validateSamplingAndMarkComplete(
        fieldsToValidate,
        fieldMeasurementErrorArray
      );
    },

    // Patch and emitPatch functions

    // This function is specifically for well sampling updates, nested items such as field measurements and sampling signatures will be handled in another actions setup.
    updateWellSample: (wellId, fieldsAndValues, emitPatch = null) => {
      const event = api.getCurrentEvent();
      if (!event) return { ok: false, error: "no_event" };

      const well = event.samplingEventWells.find((w) => w.id === wellId);
      if (!well) return { ok: false, error: "well_not_found" };
      console.log("fieldsAndValues", fieldsAndValues);
      // Putting this here, because our switch case will update multiple fields sometimes based on user input and actions.
      const fieldsToUpdate = fieldsAndValues.map(({ field }) => field);

      // Big old switch case for field updates
      fieldsAndValues.forEach(({ field, value }) => {
        switch (field) {
          case "wellSampleId":
            well.wellSampleId = value;
            break;
          case "weatherConditions":
            well.setWeatherConditions?.(value);
            break;
          case "precipitation":
            well.precipitation = value;
            break;
          case "samplingStartDate":
            well.setSamplingStartDate?.(value);
            break;
          case "samplingStartTime":
            well.setSamplingStartTime?.(value);
            break;
          case "samplingEndDate":
            well.setSamplingEndDate?.(value);
            break;
          case "samplingEndTime":
            well.setSamplingEndTime?.(value);
            break;
          case "sampleTargetDepth":
            well.setSampleTargetDepth?.(value);
            break;
          case "sampleTargetDepthNm":
            well.setSampleTargetDepthNm?.(value);
            if (value) {
              well.setSampleTargetDepth?.(null);
              fieldsToUpdate.push("sampleTargetDepth");
            }
            break;
          case "extractionRate":
            well.setExtractionRate?.(value);
            break;
          case "extractionRateNm":
            well.setExtractionRateNm?.(value);
            if (value) {
              well.setExtractionRate?.(null);
              fieldsToUpdate.push("extractionRate");
            }
            break;
          case "volumePurged":
            well.setVolumePurged?.(value);
            break;
          case "lowFlow":
            well.setLowFlow?.(value);
            break;
          case "purgeThreeWvs":
            well.setPurgeThreeWvs?.(value);
            if (!value) {
              well.setPurgeVolumeIfPurgeThreeWvs?.(null);
              fieldsToUpdate.push("purgeVolumeIfPurgeThreeWvs");
            }
            break;
          case "purgeVolumeIfPurgeThreeWvs":
            well.setPurgeVolumeIfPurgeThreeWvs?.(value);
            break;
          case "purgeDevice":
            well.setPurgeDevice?.(value);
            break;
          case "purgeDeviceOther":
            well.setPurgeDeviceOther?.(value);
            break;
          case "samplingDevice":
            well.setSamplingDevice?.(value);
            break;
          case "samplingDeviceOther":
            well.setSamplingDeviceOther?.(value);
            break;
          case "sampleFiltered":
            well.setSampleFiltered?.(value);
            break;
          case "dedicatedEquipment":
            well.setDedicatedEquipment?.(value);
            break;

          // Well data
          case "measuredDepthToWaterSampling":
            well.setMeasuredDepthToWaterSampling?.(value);
            break;
          case "measuredTotalDepthSampling":
            well.setMeasuredTotalDepthSampling?.(value);
            break;
          case "samplingBottomConditions":
            well.setSamplingBottomConditions?.(value);
            break;

          // NAPL
          case "lnaplPresent":
            well.setLnaplPresent?.(value);
            if (!value) {
              well.setLnaplThickness?.(null);
              fieldsToUpdate.push("lnaplThickness");
              well.setLnaplOdor?.("");
              fieldsToUpdate.push("lnaplOdor");
              well.setLnaplColor?.("");
              fieldsToUpdate.push("lnaplColor");
              well.setLnaplOther?.("");
            }
            break;
          case "lnaplThickness":
            well.setLnaplThickness?.(value);
            break;
          case "lnaplOdor":
            well.setLnaplOdor?.(value);
            break;
          case "lnaplColor":
            well.setLnaplColor?.(value);
            break;
          case "lnaplOther":
            well.setLnaplOther?.(value);
            break;
          case "dnaplPresent":
            well.setDnaplPresent?.(value);
            if (!value) {
              well.setDnaplThickness?.(null);
              fieldsToUpdate.push("dnaplThickness");
              well.setDnaplOdor?.("");
              fieldsToUpdate.push("dnaplOdor");
              well.setDnaplColor?.("");
              fieldsToUpdate.push("dnaplColor");
              well.setDnaplOther?.("");
              fieldsToUpdate.push("dnaplOther");
            }
            break;
          case "dnaplThickness":
            well.setDnaplThickness?.(value);
            break;
          case "dnaplOdor":
            well.setDnaplOdor?.(value);
            break;
          case "dnaplColor":
            well.setDnaplColor?.(value);
            break;
          case "dnaplOther":
            well.setDnaplOther?.(value);
            break;

          // Sample Information
          case "unableToCollectSample":
            well.setUnableToCollectSample?.(value);
            break;
          case "unableToCollectSampleReason":
            well.setUnableToCollectSampleReason?.(value);
            break;
          case "sampleTime":
            well.setSampleTime?.(value);
            break;
          case "sampleDate":
            well.setSampleDate?.(value);
            break;
          case "qcSamples":
            well.setQcSamples?.(value);
            break;
          case "sampleAppearance":
            well.setSampleAppearance?.(value);
            break;
          case "sampleColor":
            well.setSampleColor?.(value);
            break;
          case "sampleOdor":
            well.setSampleOdor?.(value);
            break;
          case "sampleComments":
            well.setSampleComments?.(value);
            break;
          case "samplingCompleted":
            well.setSamplingCompleted?.(value);
            break;
          case "samplingCompletedDate":
            well.setSamplingCompletedDate?.(value);
            break;

          // QC Sampling signature
          case "qcSamplingSignatureDate":
            well.setQcSamplingSignatureDate?.(value);
            break;
          case "qcSamplingSignatureTime":
            well.setQcSamplingSignatureTime?.(value);
            break;
          case "qcSamplingSignatureEmployeeId":
            well.setQcSamplingSignatureEmployeeId?.(value);
            break;
          case "qcSamplingSignatureEmployeeName":
            well.setQcSamplingSignatureEmployeeName?.(value);
            break;
          case "qcSamplingSignature":
            well.setQcSamplingSignature?.(value);
            break;
          case "qcSamplingSignatureDate":
            well.setQcSamplingSignatureDate?.(value);
            break;
          case "qcSamplingSignatureTime":
            well.setQcSamplingSignatureTime?.(value);
            break;
          case "qcSamplingSignatureEmployeeId":
            well.setQcSamplingSignatureEmployeeId?.(value);
            break;
          case "qcSamplingSignatureEmployeeName":
            well.setQcSamplingSignatureEmployeeName?.(value);
            break;
          case "qcSamplingSignature":
            well.setQcSamplingSignature?.(value);
            break;

          default:
            return { ok: false, error: "invalid_field" };
        }
      });

      // Emit the patch to the server
      if (emitPatch) {
        // Apply the patch to the well and return the patch, only need to return the values of the updated fields
        const patch = well.baseWellPatch("update", fieldsToUpdate);
        console.log("patch", patch);
        emitPatch(patch);
      }

      api.bumpRevision();

      return { ok: true, well };
    },

    /**
     * Handle incoming websocket broadcast for well_update patch_type.
     * Mutates the Wells class instance directly using setter methods.
     * @param {object} patch - The patch data with snake_case keys
     * @param {function} applyPatchToObject - Helper to apply patch using setters
     */
    handleBroadcast: (patch, applyPatchToObject) => {
      const event = api.getCurrentEvent();
      if (!event) return;

      const { id, action } = patch;
      const wells = event.samplingEventWells || [];

      // Handle add action - create new Well
      if (action === "add") {
        const newWell = new Wells(patch);
        wells.push(newWell);
        api.bump();
        return;
      }

      // Handle remove action - delete the Well
      if (action === "remove" || action === "delete") {
        const idx = wells.findIndex((w) => w.id === id);
        if (idx >= 0) {
          wells.splice(idx, 1);
          api.bump();
        }
        return;
      }

      // Handle update - find existing well and update via setters
      const well = wells.find((w) => w.id === id);
      if (!well) {
        // Well doesn't exist - shouldn't happen for updates, log warning
        console.warn("handleBroadcast: well not found for id:", id);
        return;
      }

      // Apply patch fields using setter methods
      applyPatchToObject(well, patch, ["id", "action", "sampling_event_id"]);
      api.bump();
    },

    /**
     *
     * @param {*} patchType // Either field_measurement || sampler_signater
     * @param {*} patch // Data coming in from the server
     * @param {*} parentWellId // parent well id to update the class items with
     * @param {*} applyPatchToObject // wrapper function to help match up data in the classes.
     */
    handleNestedBroadcast: (patchType, patch, applyPatchToObject) => {
      // You are in the middle of setting up this nested function to fix up the field measurements and teh sampler signature items. Once finished up here, you will need to add those sections to the api side of the web socket implementation setup.
      // Handling field measurement patches that come through
      if (patchType === "sampling_event_field_measurements") {
        const event = api.getCurrentEvent();
        if (!event) return;
        const well = event.samplingEventWells.find(
          (w) => w.id === patch.sampling_event_well_id
        );
        if (!well) return;

        // Setting up the "add" action
        if (patch.action === "add") {
          // Check if measurement already exists (prevent duplicates from double-adds or broadcasts)
          const existingMeasurement = well.fieldMeasurements.find(
            (fm) => fm.rowId === patch.id
          );
          if (existingMeasurement) {
            // Update existing instead of creating duplicate
            applyPatchToObject(existingMeasurement, patch, [
              "id",
              "action",
              "sampling_event_well_id",
            ]);
            api.bump();
            return;
          }
          const newFieldMeasurement = new FieldMeasurements({
            id: patch.id,
            sampling_event_well_id: patch.sampling_event_well_id,
            sampling_event_id: patch.sampling_event_id,
            well_id: patch.well_id,
            sort_order: patch.sort_order,
          });
          // Apply all patch fields to the new measurement
          applyPatchToObject(newFieldMeasurement, patch, [
            "id",
            "action",
            "sampling_event_well_id",
          ]);
          well.fieldMeasurements.push(newFieldMeasurement);
          api.bump();
          return;
        }

        // Setting up the "update" action
        if (patch.action === "update") {
          const fieldMeasurement = well.fieldMeasurements.find(
            (fm) => fm.rowId === patch.id
          );
          if (!fieldMeasurement) return;
          applyPatchToObject(fieldMeasurement, patch, [
            "id",
            "action",
            "sampling_event_well_id",
          ]);
          api.bump();
          return;
        }
        // Setting up the "remove" action
        if (patch.action === "remove") {
          well.removeFieldMeasurement(patch.id);
          api.bump();
          return;
        }
      }

      // Handling sampler signature patches that come through
      if (patchType === "sampling_event_sampling_employee_signatures") {
        console.log("handling sampler signature patches");
        const event = api.getCurrentEvent();
        if (!event) return;
        const well = event.samplingEventWells.find(
          (w) => w.id === patch.sampling_event_well_id
        );
        if (!well) return;
        // Setting up the "add" action
        if (patch.action === "add") {
          const newSamplerSignature = new SamplingEmployeesSignature({
            id: patch.id,
            sampling_event_well_id: patch.sampling_event_well_id,
            sampling_event_id: patch.sampling_event_id,
            employee_id: patch.employee_id,
            employee_name: patch.employee_name,
            signature: patch.signature,
          });
          well.employeeSignatures.push(newSamplerSignature);
          api.bump();
          return;
        }

        // Setting up the "update" action
        if (patch.action === "update") {
          const samplerSignature = well.employeeSignatures.find(
            (ss) => ss.rowId === patch.id
          );
          if (!samplerSignature) return;
          applyPatchToObject(samplerSignature, patch, [
            "id",
            "action",
            "sampling_event_well_id",
          ]);
          api.bump();
          return;
        }
        // Setting up the "remove" action
        if (patch.action === "remove") {
          well.removeEmployeeSignature(patch.id);
          api.bump();
          return;
        }
      }
    },
  };
};
